import { ENVIRONMENT_LABEL } from '../schemas/environment';

import type { FlagAuditEntry, FlagAuditSnapshot } from '../schemas/flag';

/**
 * Turn one audit entry into the sentence the history list shows.
 *
 * Split out of `FlagHistory.tsx` because it is the only part of that component
 * with anything to get wrong. The API sends structured before/after snapshots
 * rather than pre-written text — which is what makes the log worth keeping — so
 * the prose is derived, and the derivation carries a distinction the log exists
 * for:
 *
 * `before: null` means **no row existed**, which is not the same fact as "the
 * row existed and was off". Collapsing the two would make a first write
 * indistinguishable from a toggle, and telling those apart months later is the
 * point of keeping a log at all.
 */
export function describeAuditEntry(entry: FlagAuditEntry): string {
    switch (entry.action) {
        case 'create-flag':
            return 'Created this flag';
        case 'update-definition':
            return describeDefinitionChange(entry.before, entry.after);
        case 'set-default':
            return `Platform default — ${describeStateChange(entry.before, entry.after)}`;
        case 'set-environment-default':
            return `${environmentLabel(entry.environment)} default — ${describeStateChange(entry.before, entry.after)}`;
        case 'clear-environment-default':
            // Mirrors the `clear-override` idempotent-clear distinction below:
            // the API records "asked to clear, there was nothing there" as its
            // own fact rather than folding it into "removed the row".
            return entry.before === null
                ? `Cleared the ${environmentLabel(entry.environment)} default (there was none)`
                : `Reverted the ${environmentLabel(entry.environment)} default to the platform default`;
        case 'set-override':
            return `${entry.tenantId ?? 'A museum'} — ${describeStateChange(entry.before, entry.after)}`;
        case 'clear-override':
            // `before === null` is the idempotent clear: somebody asked to
            // revert a museum that was already on the default. The API records
            // it deliberately, so the history must not render it as if an
            // override had been removed.
            return entry.before === null
                ? `Cleared ${entry.tenantId ?? 'a museum'}'s override (there was none)`
                : `Reverted ${entry.tenantId ?? 'a museum'} to the platform default`;
    }
}

/**
 * `entry.environment` is optional in the schema because most actions never
 * carry it, but the API guarantees it is always set on the two actions that
 * read it here (see the field's docblock in `schemas/flag.ts`). The fallback
 * is defensive only — a history line should never go blank because a field
 * disagreed with its own contract.
 */
function environmentLabel(environment: FlagAuditEntry['environment']): string {
    return environment !== undefined ? ENVIRONMENT_LABEL[environment] : 'This environment';
}

function describeStateChange(
    before: FlagAuditSnapshot | null,
    after: FlagAuditSnapshot | null,
): string {
    if (after === null || after.type !== 'state') return 'cleared';

    const next = after.state;
    const previous = before?.type === 'state' ? before.state : null;

    const parts: string[] = [];

    if (previous === null) {
        parts.push(next.enabled ? 'set to on (first time)' : 'set to off (first time)');
    } else if (previous.enabled !== next.enabled) {
        parts.push(next.enabled ? 'turned on' : 'turned off');
    }

    if (previous?.rolloutPercentage !== next.rolloutPercentage) {
        parts.push(
            next.rolloutPercentage === undefined
                ? 'rollout gate removed'
                : `rollout set to ${String(next.rolloutPercentage)}%`,
        );
    }

    // Normalised: the snapshot type marks `archived` optional (override
    // snapshots never carry it), so `false` and absent are the same fact and
    // must not read as an un-archive.
    if (previous !== null && (previous.archived ?? false) !== (next.archived ?? false)) {
        parts.push(next.archived === true ? 'archived' : 'un-archived');
    }

    // A write that changed nothing observable is still a real event — somebody
    // saved this row. Saying so is more honest than rendering an empty line.
    return parts.length > 0 ? parts.join(', ') : 'saved with no change';
}

function describeDefinitionChange(
    before: FlagAuditSnapshot | null,
    after: FlagAuditSnapshot | null,
): string {
    if (before?.type !== 'definition' || after?.type !== 'definition') return 'Edited the details';

    const changed = (['name', 'description', 'category', 'lifecycle', 'owner'] as const).filter(
        (field) => before.definition[field] !== after.definition[field],
    );

    if (changed.length === 0) return 'Saved the details with no change';
    if (changed.includes('lifecycle') && changed.length === 1) {
        return `Lifecycle → ${after.definition.lifecycle}`;
    }

    return `Edited ${changed.join(', ')}`;
}
